import { useState } from "react";
import { Check, X, UserPlus, Network, Loader2 } from "lucide-react";
import Select from "../../components/common/Select";
import { useToast } from "../../components/ToastContext";
import { createCompanyUser } from "../../api/services/companyAdminService";
import ScrollArea from "../../components/common/ScrollArea";

const defaultForm = {
  first_name: "",
  last_name: "",
  username: "",
  email: "",
  designation: "",
  allow_topology_access: false,
  allow_scenario_creation: false,
  team_id: "",
};

export default function AddUserModal({
  isOpen,
  teams,
  isLoadingTeams,
  onClose,
  onSuccess,
}) {
  const { showToast } = useToast();
  const [formData, setFormData] = useState(defaultForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const teamOptions = teams.map((team) => ({
    value: team.id,
    label: team.name,
  }));

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const togglePermission = (key) => {
    setFormData((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleClose = () => {
    setSubmitError(null);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await createCompanyUser(formData);
      setFormData(defaultForm);
      onClose();
      showToast(
        "User created successfully! An email has been sent to them for setting their password.",
        "success",
        5000,
      );
      onSuccess();
    } catch (error) {
      setSubmitError(
        error.message || "Failed to create user. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-(--color-bg-overlay) flex items-center justify-center z-50 p-4">
      <div className="bg-(--color-bg)/50 backdrop-blur-md rounded-md shadow-2xl w-full max-w-3xl border border-(--color-border) overflow-hidden flex flex-col max-h-[calc(100vh-2rem)]">
        {/* Header — always visible */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-(--color-border) shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-(--color-primary)/10 flex items-center justify-center shrink-0">
              <UserPlus className="w-4 h-4 text-(--color-primary)" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-(--color-text-primary) leading-tight">
                Add single user
              </h2>
              <p className="text-xs text-(--color-text-muted) mt-0.5">
                New user will receive a password setup email
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-7 h-7 flex items-center justify-center text-(--color-text-muted) hover:text-(--color-text-primary) hover:bg-(--color-bg-overlay) transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Scrollable body */}
          <ScrollArea className="flex-1 min-h-0">
            <div className="px-5 sm:px-6 py-4 space-y-4">
              {/* Personal Info */}
              <div>
                <p className="text-[10.5px] font-semibold uppercase tracking-widest text-(--color-text-primary) mb-2.5">
                  Personal info
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-(--color-text-muted)">
                      First name
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      placeholder="Jane"
                      required
                      className="px-3 py-2 text-sm border border-(--color-border) rounded-md bg-(--color-bg-card) text-(--color-text-primary) placeholder:text-(--color-text-muted)/50 focus:outline-none focus:ring-2 focus:ring-(--color-primary)/40 focus:border-(--color-primary) transition-all"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-(--color-text-muted)">
                      Last name
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      placeholder="Doe"
                      required
                      className="px-3 py-2 text-sm border border-(--color-border) rounded-md bg-(--color-bg-card) text-(--color-text-primary) placeholder:text-(--color-text-muted)/50 focus:outline-none focus:ring-2 focus:ring-(--color-primary)/40 focus:border-(--color-primary) transition-all"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-(--color-text-muted)">
                      Username
                    </label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      placeholder="john_doe"
                      required
                      className="px-3 py-2 text-sm border border-(--color-border) rounded-md bg-(--color-bg-card) text-(--color-text-primary) placeholder:text-(--color-text-muted)/50 focus:outline-none focus:ring-2 focus:ring-(--color-primary)/40 focus:border-(--color-primary) transition-all"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-(--color-text-muted)">
                      Designation
                    </label>
                    <input
                      type="text"
                      name="designation"
                      value={formData.designation}
                      onChange={handleInputChange}
                      placeholder="Engineer"
                      required
                      className="px-3 py-2 text-sm border border-(--color-border) rounded-md bg-(--color-bg-card) text-(--color-text-primary) placeholder:text-(--color-text-muted)/50 focus:outline-none focus:ring-2 focus:ring-(--color-primary)/40 focus:border-(--color-primary) transition-all"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-(--color-text-muted)">
                    Email address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@company.com"
                    required
                    className="px-3 py-2 text-sm border border-(--color-border) rounded-md bg-(--color-bg-card) text-(--color-text-primary) placeholder:text-(--color-text-muted)/50 focus:outline-none focus:ring-2 focus:ring-(--color-primary)/40 focus:border-(--color-primary) transition-all"
                  />
                </div>
              </div>

              <div className="h-px bg-(--color-border)" />

              {/* Team */}
              <div>
                <p className="text-[10.5px] font-semibold uppercase tracking-widest text-(--color-text-primary) mb-2.5">
                  Team
                </p>
                <Select
                  name="team_id"
                  value={formData.team_id}
                  onChange={handleInputChange}
                  options={teamOptions}
                  placeholder={
                    isLoadingTeams ? "Loading teams..." : "Select a team"
                  }
                  icon={Network}
                  className="space-y-1"
                  labelClassName="text-xs font-medium text-(--color-text-muted) mb-1.5"
                  triggerClassName="h-10 px-3 rounded-md bg-(--color-bg-card) border border-(--color-border)"
                  valueClassName="text-sm"
                  chevronClassName="w-4 h-4"
                  menuClassName="mt-1 rounded-md bg-(--color-bg-card)"
                  optionClassName="py-2.5"
                  label="Assign to team"
                />
              </div>

              <div className="h-px bg-(--color-border)" />

              {/* Permissions */}
              <div>
                <p className="text-[10.5px] font-semibold uppercase tracking-widest text-(--color-text-primary) mb-2.5">
                  Permissions
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-2.5">
                  {" "}
                  {[
                    {
                      key: "allow_topology_access",
                      label: "Topology access",
                      desc: "Access topology workspace",
                    },
                    {
                      key: "allow_scenario_creation",
                      label: "Box creation",
                      desc: "Create new Box",
                    },
                  ].map(({ key, label, desc }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => togglePermission(key)}
                      className={`flex items-center gap-3 p-3 rounded-md border text-left transition-all ${
                        formData[key]
                          ? "border-(--color-primary) bg-(--color-primary)/8"
                          : "border-(--color-border) hover:bg-(--color-bg-overlay)"
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded-sm border-[1.5px] flex items-center justify-center shrink-0 transition-all ${
                          formData[key]
                            ? "bg-(--color-primary) border-(--color-primary)"
                            : "border-(--color-border)"
                        }`}
                      >
                        {formData[key] && (
                          <Check
                            className="w-2.5 h-2.5 text-white dark:text-black"
                            strokeWidth={3}
                          />
                        )}
                      </div>
                      <div>
                        <div className="text-xs font-medium text-(--color-text-primary) leading-tight">
                          {label}
                        </div>
                        <div className="text-[10.5px] text-(--color-text-muted) mt-0.5">
                          {desc}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {submitError && (
              <div className="mx-6 mb-3 p-3 bg-red-500/10 border border-red-500/30 rounded-md text-red-400 text-xs">
                {submitError}
              </div>
            )}
          </ScrollArea>

          {/* Footer — always pinned */}
          <div className="px-5 sm:px-6 py-3.5 border-t border-(--color-border) flex flex-col sm:flex-row gap-2.5 shrink-0">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 border border-(--color-border) text-(--color-text-primary) rounded-md hover:bg-(--color-bg-overlay) transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 bg-(--color-primary) text-white dark:text-black rounded-md hover:opacity-90 transition-colors flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Creating...
                </>
              ) : (
                <>
                  <Check className="w-3.5 h-3.5" strokeWidth={2.5} /> Add user
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
